import { Component, Inject, OnInit, OnDestroy } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { GlobalConstants } from "../../../common/global-constants";
import { AuthenticationService } from "../../../services/authentication.service";
import {
  uiPageTypes,
  uiPageTypeList,
  requestAccessTypeList,
  userConsentEnum,
  viewLabelValues,
  requestPermissionFormConstants,
} from "../../../common/common-constants";
import { CommonUtils } from "../../../utils/common.utils";
import { DataService } from "../../../services/data.service";
import {
  accessControlResourceMapper,
  namespace,
  personActorType,
  RESOURCE_NAME_SEPARATOR,
} from "../../../common/manage-permission";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-request-permissions",
  templateUrl: "./request-permissions.component.html",
  styleUrls: ["./request-permissions.component.scss"],
})
export class RequestPermissionsComponent implements OnInit, OnDestroy {
  public requestPermissionForm: FormGroup;
  public loading = false;
  public uiPageTypes: object = uiPageTypes;
  public uiPageTypeList: object[] = uiPageTypeList;
  public requestResourcePermissionURL =
    GlobalConstants.REQUEST_RESOURCE_PERMISSION_ENDPOINT;
  public fromCountryCodeOptions: any = [];
  public transitTimeConfigurationRecords: any = [];
  public currentWorkingRecord: any;
  public dropDownOptions: any = {};
  public dropDownShipmethodOptions: any = [];
  public user: any;
  public currentComponentKeyPlaceholder: string[] = [];
  public requestAccessTypeList: object[] = requestAccessTypeList;
  public transitTimeOptions: any = [];
  public userConsentEnum = userConsentEnum;
  public allCarriers: string = userConsentEnum.YES;
  public userConsentSelectOptions: string[] = [
    userConsentEnum.YES,
    userConsentEnum.NO,
  ];
  public viewLabelValues = viewLabelValues;
  public requestPermissionFormConstants = requestPermissionFormConstants;
  private destroy$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    @Inject("RequestsService") private requestsService,
    @Inject("AuthenticationService")
    private readonly authenticationService: AuthenticationService,
    private toastrService: ToastrService,
    @Inject("CommonUtils") private readonly commonUtils: CommonUtils,
    private dataService: DataService,
  ) {}

  ngOnInit(): void {
    this.requestPermissionForm = this.formBuilder.group({
      requestAccessType: [viewLabelValues.otherRequest, Validators.required],
      uiPageType: [[], Validators.required],
      fromCountryCode: [null, Validators.required],
      businessUseCase: [null, Validators.required],
      componentKeyPlaceholder: [[], Validators.required],
      componentKeyValue: [[], Validators.required],
      transitTimeType: [null, Validators.required],
      requestReason: ["", Validators.required],
      componentKeyValueSelector: [],
    });
    this.fromCountryCodeOptions = this.commonUtils.getFromCountryCodes();
    this.user = this.authenticationService.getUserDetails();
    this.transitTimeOptions = this.dataService.transitTimeOptions;
  }

  // updateFormControls update both values and validators
  private updateFormControls(config: {
    [key: string]: { value?: any; validators?: ValidatorFn[] | null };
  }) {
    Object.entries(config).forEach(([controlName, settings]) => {
      const control = this.requestPermissionForm.get(controlName);
      if (settings.value !== undefined) {
        control.setValue(settings.value);
      }
      if (settings.validators !== undefined) {
        if (settings.validators) {
          control.setValidators(settings.validators);
        } else {
          control.clearValidators();
        }
      }
      control.updateValueAndValidity();
    });
  }

  private resetRequestPermissionForm() {
    const requestAccessTypeControl = this.requestPermissionForm.get(
      requestPermissionFormConstants.requestAccessType,
    );

    //patchValue only updates VALUES — it does NOT change validators
    this.requestPermissionForm.patchValue({
      fromCountryCode: "",
      businessUseCase: null,
      requestReason: "",
    });

    if (requestAccessTypeControl?.value === viewLabelValues.otherRequest) {
      this.updateFormControls({
        uiPageType: { value: [], validators: [Validators.required] },
        componentKeyPlaceholder: {
          value: [],
          validators: [Validators.required],
        },
        transitTimeType: { value: null, validators: [Validators.required] },
      });
      this.requestPermissionForm.setControl(
        requestPermissionFormConstants.componentKeyValue,
        new FormControl([], Validators.required),
      );
    } else {
      this.updateFormControls({
        uiPageType: { value: [], validators: null },
        componentKeyPlaceholder: { value: [], validators: null },
        transitTimeType: { value: null, validators: null },
      });
      this.requestPermissionForm.setControl(
        requestPermissionFormConstants.componentKeyValue,
        new FormControl([]),
      );
    }

    this.currentComponentKeyPlaceholder = [];
  }

  public onSelectOptionUpdated(selectedValues: any[], field: string) {
    const componentKeyValueGroup = this.requestPermissionForm.get(
      requestPermissionFormConstants.componentKeyValue,
    ) as FormGroup;
    componentKeyValueGroup.get(field)?.setValue(selectedValues);
  }

  public onRequestAccessTypeChange() {
    this.resetRequestPermissionForm();
  }

  public onUIPageTypeChange(uiPage) {
    const uiPageTypeControl = this.requestPermissionForm.get(
      requestPermissionFormConstants.uiPageType,
    );
    if (uiPageTypeControl.value.some((uiPageType) => uiPageType === uiPage)) {
      uiPageTypeControl.setValue(
        uiPageTypeControl.value.filter((item) => item !== uiPage),
      );
    } else {
      uiPageTypeControl.setValue([...uiPageTypeControl.value, uiPage]);
    }
  }

  public onFromCountryCodeChange(fromCountryCode: string) {
    this.loading = true;
    const requestAccessTypeControl = this.requestPermissionForm.get(
      requestPermissionFormConstants.requestAccessType,
    );

    if (requestAccessTypeControl?.value === viewLabelValues.otherRequest) {
      this.requestPermissionForm.patchValue({
        businessUseCase: null,
        componentKeyPlaceholder: [],
        componentKeyValue: [],
        transitTimeType: "",
      });
    } else {
      this.requestPermissionForm.patchValue({
        businessUseCase: null,
      });
    }

    this.dataService
      .getConfigurationRecordsBasedOnFromCountryCode(fromCountryCode)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data: any[]) => {
          this.loading = false;
          this.transitTimeConfigurationRecords =
            data[GlobalConstants.TRANSIT_TIME_CONFIGURATIONS_RESPONSE_KEY];
        },
        (error) => {
          this.loading = false;
          this.toastrService.error(error, GlobalConstants.TITLE_ERROR);
        },
      );
  }

  public onBusinessUseCaseChange(businessUseCase) {
    const businessUseCaseValue = JSON.parse(businessUseCase);
    this.currentWorkingRecord = this.commonUtils.getCurrentWorkingRecord(
      businessUseCaseValue?.value,
    );
    this.dropDownOptions = this.commonUtils.getDropDownOptions(
      this.currentWorkingRecord,
      this.transitTimeConfigurationRecords,
    );
    this.currentComponentKeyPlaceholder = this.currentWorkingRecord[
      GlobalConstants.COMPONENT_KEY_PLACE_HOLDER
    ]?.split(GlobalConstants.COMPONENT_KEY_VALUE_SEPARATOR);

    const requestAccessTypeControl = this.requestPermissionForm.get(
      requestPermissionFormConstants.requestAccessType,
    );

    if (requestAccessTypeControl?.value === viewLabelValues.otherRequest) {
      this.requestPermissionForm.patchValue({
        componentKeyPlaceholder: this.currentComponentKeyPlaceholder,
        transitTimeType: "",
      });
      this.requestPermissionForm.setControl(
        requestPermissionFormConstants.componentKeyValue,
        this.getComponentKeyValueFormGroup(),
      );
      this.requestPermissionForm.setControl(
        requestPermissionFormConstants.componentKeyValueSelector,
        this.getComponentKeyValueSelectorFormGroup(),
      );
      this.onComponentKeyValueSelectorChangeTwo();
    }
  }

  private getComponentKeyValueFormGroup() {
    let componentKeyPlaceValueFormGroupInput = {};
    for (let componentKey of this.currentComponentKeyPlaceholder) {
      componentKeyPlaceValueFormGroupInput[componentKey] = new FormControl(
        [],
        Validators.required,
      );
    }
    return this.formBuilder.group(componentKeyPlaceValueFormGroupInput);
  }

  private getComponentKeyValueSelectorFormGroup() {
    let componentKeyPlaceValueFormGroupInput = {};
    for (let componentKey of this.currentComponentKeyPlaceholder) {
      componentKeyPlaceValueFormGroupInput[componentKey] = new FormControl(
        userConsentEnum.YES,
      );
    }
    return this.formBuilder.group(componentKeyPlaceValueFormGroupInput);
  }

  get getItems() {
    return this.commonUtils.getItemsBasedOnArc();
  }

  private getFormattedResource(type, name, operations) {
    return {
      resourceNamespace: namespace,
      resourceType: type,
      resourceName: name,
      templates: operations,
    };
  }

  private formatByresourceNameSeperator(...resourceNameList) {
    return resourceNameList.reduce(
      (accumulator, resourceName) =>
        `${accumulator}${RESOURCE_NAME_SEPARATOR}${resourceName}`,
    );
  }

  private getResourceName(
    componentKeyValueList: string[][],
    resourceNameList: string[],
    resourceName: string,
    i: number,
  ) {
    let keyValues = componentKeyValueList[i];
    i += 1;

    if (keyValues?.length) {
      for (let keyValue of keyValues) {
        let resourceNameResult = `${resourceName}:${keyValue}`;
        this.getResourceName(
          componentKeyValueList,
          resourceNameList,
          resourceNameResult,
          i,
        );
      }
    } else {
      resourceNameList.push(resourceName);
    }

    return resourceNameList;
  }

  public onComponentKeyValueSelectorChangeOne(
    componentSelectorKey: string,
    componentSelectorValue: string,
  ) {
    this.resetComponentKeyValueGroup();

    if (componentSelectorValue === userConsentEnum.YES) {
      this.componentKeyValueFormGroup
        .get(componentSelectorKey)
        ?.setValue([userConsentEnum.ALL]);
    }
  }

  public onComponentKeyValueSelectorChangeTwo() {
    this.resetComponentKeyValueGroup();

    const isAllCarriers = this.componentKeyValueSelectorFormGroup.get(
      this.carrierComponentKeyVaue,
    )?.value;
    const isAllShipmethods = this.componentKeyValueSelectorFormGroup.get(
      this.shipmethodComponentKeyVaue,
    )?.value;

    if (isAllCarriers === userConsentEnum.YES) {
      for (let componentKey of this.currentComponentKeyPlaceholder) {
        this.componentKeyValueFormGroup
          .get(componentKey)
          ?.setValue([userConsentEnum.ALL]);
      }
    }

    if (isAllCarriers === userConsentEnum.NO) {
      if (isAllShipmethods === userConsentEnum.YES) {
        this.componentKeyValueFormGroup
          .get(this.shipmethodComponentKeyVaue)
          ?.setValue([userConsentEnum.ALL]);
      }
      if (isAllShipmethods === userConsentEnum.NO) {
        this.componentKeyValueFormGroup
          .get(this.carrierComponentKeyVaue)
          ?.setValue("");
      }
    }
  }

  public updateShipmethodOnCarrierNameChange(carrierName: string) {
    this.componentKeyValueFormGroup
      .get(this.shipmethodComponentKeyVaue)
      ?.setValue([]);
    this.dropDownShipmethodOptions =
      this.transitTimeConfigurationRecords.reduce((shipmethodList, record) => {
        const componentKeyValueList = record[
          GlobalConstants.COMPONENT_KEY_VALUE
        ]?.split(GlobalConstants.COMPONENT_KEY_VALUE_SEPARATOR);

        if (
          record[GlobalConstants.BUSINESS_UNIT] ===
            this.currentWorkingRecord[GlobalConstants.BUSINESS_UNIT] &&
          componentKeyValueList?.[0] === carrierName
        ) {
          shipmethodList.push(componentKeyValueList?.[1]);
        }

        return shipmethodList;
      }, []);
  }

  private resetComponentKeyValueGroup() {
    for (let componentKey of this.currentComponentKeyPlaceholder) {
      this.componentKeyValueFormGroup.get(componentKey)?.setValue([]);
    }
  }

  public get componentKeyValueFormGroup() {
    return this.requestPermissionForm.get(
      requestPermissionFormConstants.componentKeyValue,
    ) as FormGroup;
  }

  public get componentKeyValueSelectorFormGroup() {
    return this.requestPermissionForm.get(
      requestPermissionFormConstants.componentKeyValueSelector,
    ) as FormGroup;
  }

  public get carrierComponentKeyVaue() {
    return this.currentComponentKeyPlaceholder[0] || "";
  }

  public get shipmethodComponentKeyVaue() {
    return this.currentComponentKeyPlaceholder[1] || "";
  }

  public submit() {
    const {
      requestAccessType,
      uiPageType,
      fromCountryCode,
      businessUseCase,
      componentKeyValueSelector,
      componentKeyValue,
      transitTimeType,
      requestReason,
    } = JSON.parse(JSON.stringify(this.requestPermissionForm.value));
    const businessUseCaseValue = JSON.parse(businessUseCase);
    const businessUnitResourceValue =
      accessControlResourceMapper["businessUnitResource"];
    const componentKeyValueResourceValue =
      accessControlResourceMapper["componentKeyValueResource"];
    let requestedPermissions = [];

    if (requestAccessType === viewLabelValues.otherRequest) {
      let operations = uiPageType.map(
        (uiPage) => `${uiPage}_${transitTimeType}`,
      );
      let componentKeyValueList = [];

      /**
       * in case NO is selected for both all carrier name and all shipmethod
       * then wrap the carrierName text value into array to make it consistent
       * */
      if (
        componentKeyValueSelector[this.carrierComponentKeyVaue] ===
          userConsentEnum.NO &&
        componentKeyValueSelector[this.shipmethodComponentKeyVaue] ===
          userConsentEnum.NO
      ) {
        componentKeyValue[this.carrierComponentKeyVaue] = [
          componentKeyValue[this.carrierComponentKeyVaue],
        ];
      }

      for (const componentKey in componentKeyValue) {
        const componentValue = componentKeyValue[componentKey];
        if (componentValue?.length > 0) {
          componentKeyValueList.push(componentValue);
        }
      }

      /**
       * in case YES is selected for all carrier name
       * then merge both carrierName and shipmethod ALL value into single value
       */
      if (
        componentKeyValueList?.length === 2 &&
        componentKeyValueList?.[0]?.[0] === userConsentEnum.ALL &&
        componentKeyValueList?.[1]?.[0] === userConsentEnum.ALL
      ) {
        componentKeyValueList = [[userConsentEnum.ALL]];
      }

      const resourceNamePrefix = this.formatByresourceNameSeperator(
        fromCountryCode,
        businessUseCaseValue?.mapping?.businessUnit,
      );

      let componentKeyValueResourceNames = this.getResourceName(
        componentKeyValueList,
        [],
        resourceNamePrefix,
        0,
      );

      const componentKeyValuesResources = componentKeyValueResourceNames.map(
        (resourceName) =>
          this.getFormattedResource(
            componentKeyValueResourceValue?.type,
            resourceName,
            operations,
          ),
      );
      requestedPermissions = requestedPermissions.concat(
        componentKeyValuesResources,
      );
    } else {
      const businessUnitResource = this.getFormattedResource(
        businessUnitResourceValue?.type,
        this.formatByresourceNameSeperator(
          fromCountryCode,
          businessUseCaseValue?.mapping?.businessUnit,
        ),
        businessUnitResourceValue?.operations?.map(
          (operation) => `${operation}_${requestAccessType}`,
        ),
      );
      requestedPermissions.push(businessUnitResource);
    }

    // request payload mapping
    const requestBody = {
      actorId: this.user?.alias,
      actorType: personActorType,
      requestedPermissions,
      requestReason,
    };

    // Call backend apis
    this.loading = true;
    this.requestsService
      .put(this.requestResourcePermissionURL, requestBody)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data) => {
          this.loading = false;
          this.toastrService.success(
            GlobalConstants.REQUEST_SUCCESSFUL,
            GlobalConstants.TITLE_SUCCESS,
          );
        },
        (error) => {
          this.loading = false;
          this.toastrService.error(error, GlobalConstants.TITLE_ERROR);
        },
      );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
